import { useState, useMemo, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../ui/select";
import { Scale, TrendingDown, TrendingUp, Minus, Loader2, Sparkles, Euro, Save, X } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const UNITA_PESO = [
  { value: "kg", label: "kg" },
  { value: "g", label: "g" },
  { value: "hg", label: "hg" },
];
const UNITA_VOL = [
  { value: "L", label: "L" },
  { value: "ml", label: "ml" },
  { value: "cl", label: "cl" },
];
const UNITA_PZ = [{ value: "pz", label: "pz" }];

const ConfrontaPrezzoDialog = ({ open, onOpenChange, prodotto, fornitori, localeId, onSaved }) => {
  const { token } = useAuth();
  // Step: 'compare' → 'result' → 'save'
  const [step, setStep] = useState("compare");

  // Form fields (step 1)
  const [prezzoTotale, setPrezzoTotale] = useState("");
  const [ivaPct, setIvaPct] = useState("10");
  const [prezzoIvato, setPrezzoIvato] = useState(false);
  const [quantita, setQuantita] = useState("");
  const [udm, setUdm] = useState(prodotto?.unita_misura || "kg");

  // Result
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Save (step 3)
  const [fornitoreId, setFornitoreId] = useState("");
  const [fornitoreManuale, setFornitoreManuale] = useState("");
  const [saving, setSaving] = useState(false);

  const udmRef = (prodotto?.unita_misura || "kg").toLowerCase();
  const udmOptions = useMemo(() => {
    if (["kg", "g", "hg"].includes(udmRef)) return UNITA_PESO;
    if (["l", "ml", "cl"].includes(udmRef)) return UNITA_VOL;
    return UNITA_PZ;
  }, [udmRef]);

  // Reset quando si apre/chiude o cambia prodotto
  useEffect(() => {
    if (open && prodotto) {
      setUdm(prodotto.unita_misura || "kg");
    }
  }, [open, prodotto]);

  const reset = () => {
    setStep("compare");
    setPrezzoTotale(""); setIvaPct("10"); setPrezzoIvato(false);
    setQuantita(""); setUdm(prodotto?.unita_misura || "kg");
    setFornitoreId(""); setFornitoreManuale(""); setResult(null);
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(reset, 200);
  };

  const handleConfronta = async () => {
    const pt = parseFloat(prezzoTotale);
    const q = parseFloat(quantita);
    if (!pt || pt <= 0) { toast.error("Inserisci un prezzo valido"); return; }
    if (!q || q <= 0) { toast.error("Inserisci una quantità valida"); return; }
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
      const res = await fetch(`${API}/api/ingredienti/${prodotto.id}/confronta-prezzo`, {
        method: "POST", headers,
        body: JSON.stringify({
          prezzo_totale: pt,
          quantita: q,
          unita_misura: udm,
          iva_percentuale: parseFloat(ivaPct) || 0,
          prezzo_ivato: prezzoIvato,
          locale_id: localeId || null,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || "Errore confronto");
      setResult(json);
      setStep("result");
    } catch (e) {
      toast.error(e.message);
    } finally { setLoading(false); }
  };

  const handleSalva = async () => {
    const fNome = fornitori.find(f => f.id === fornitoreId)?.ragione_sociale || fornitoreManuale;
    if (!fNome) { toast.error("Seleziona o scrivi il nome del fornitore"); return; }
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
      const res = await fetch(`${API}/api/ingredienti/${prodotto.id}/prezzo`, {
        method: "POST", headers,
        body: JSON.stringify({
          ingrediente_id: prodotto.id,
          fornitore_id: fornitoreId || "manuale",
          fornitore_nome: fNome,
          prezzo_kg: result.offerta.prezzo_per_unita_ivato,
          locale_id: localeId || null,
        }),
      });
      if (!res.ok) throw new Error("Salvataggio fallito");
      toast.success(`Prezzo salvato per ${fNome}`);
      handleClose();
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.message);
    } finally { setSaving(false); }
  };

  const verdictStyle = {
    conveniente: { bg: "#ECFDF5", border: "#10B981", text: "#047857", icon: TrendingDown },
    piu_caro: { bg: "#FEF2F2", border: "#EF4444", text: "#B91C1C", icon: TrendingUp },
    stesso: { bg: "#F3F4F6", border: "#9CA3AF", text: "#4B5563", icon: Minus },
    nessun_riferimento: { bg: "#FEF3C7", border: "#F59E0B", text: "#92400E", icon: Sparkles },
  };
  const v = result?.confronto?.verdict && verdictStyle[result.confronto.verdict];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-lg w-[95vw] sm:w-full max-h-[95vh] overflow-y-auto p-4 sm:p-6" data-testid="confronta-prezzo-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Scale className="h-5 w-5 text-indigo-500 flex-shrink-0" />
            <span className="truncate">Confronta prezzo — {prodotto?.nome}</span>
          </DialogTitle>
        </DialogHeader>

        {/* ─── STEP 1: form offerta ─── */}
        {step === "compare" && (
          <div className="rounded-xl border border-slate-200 p-3 bg-slate-50/40 space-y-3" data-testid="confronta-step-compare">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Offerta del fornitore</p>

            <div>
              <label className="text-xs text-slate-600 mb-1 block font-medium">Prezzo totale (€)</label>
              <Input
                type="number" step="0.01" inputMode="decimal" placeholder="9.50"
                value={prezzoTotale} onChange={(e) => setPrezzoTotale(e.target.value)}
                className="h-12 text-base"
                data-testid="confronta-prezzo-input"
              />
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPrezzoIvato(false)}
                className={`flex-1 h-10 rounded-xl text-sm font-bold transition-all ${!prezzoIvato ? "bg-indigo-600 text-white shadow" : "bg-white text-slate-500 border border-slate-200"}`}
                data-testid="confronta-toggle-netto"
              >
                Prezzo netto
              </button>
              <button
                type="button"
                onClick={() => setPrezzoIvato(true)}
                className={`flex-1 h-10 rounded-xl text-sm font-bold transition-all ${prezzoIvato ? "bg-indigo-600 text-white shadow" : "bg-white text-slate-500 border border-slate-200"}`}
                data-testid="confronta-toggle-ivato"
              >
                Prezzo ivato
              </button>
            </div>

            <div>
              <label className="text-xs text-slate-600 mb-1 block font-medium">Aliquota IVA</label>
              <div className="flex gap-2">
                {["4", "10", "22"].map(val => (
                  <button
                    key={val}
                    type="button"
                    onClick={() => setIvaPct(val)}
                    className={`flex-1 h-10 rounded-xl text-sm font-bold transition-all ${ivaPct === val ? "bg-indigo-100 text-indigo-700 border-2 border-indigo-400" : "bg-white text-slate-500 border border-slate-200"}`}
                    data-testid={`confronta-iva-chip-${val}`}
                  >
                    {val}%
                  </button>
                ))}
                <Input
                  type="number" step="1" inputMode="decimal" placeholder="…"
                  value={["4", "10", "22"].includes(ivaPct) ? "" : ivaPct}
                  onChange={(e) => setIvaPct(e.target.value)}
                  className="w-20 h-10 text-base text-center"
                  data-testid="confronta-iva-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-[1fr_110px] gap-2">
              <div>
                <label className="text-xs text-slate-600 mb-1 block font-medium">Quantità</label>
                <Input
                  type="number" step="0.01" inputMode="decimal" placeholder="875"
                  value={quantita} onChange={(e) => setQuantita(e.target.value)}
                  className="h-12 text-base"
                  data-testid="confronta-quantita-input"
                />
              </div>
              <div>
                <label className="text-xs text-slate-600 mb-1 block font-medium">Unità</label>
                <Select value={udm} onValueChange={setUdm}>
                  <SelectTrigger className="h-12 text-base" data-testid="confronta-udm-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {udmOptions.map(u => <SelectItem key={u.value} value={u.value} className="text-base py-2.5">{u.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button
              onClick={handleConfronta}
              disabled={loading}
              className="w-full h-12 text-base font-bold"
              data-testid="confronta-btn"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Scale className="h-5 w-5 mr-2" />}
              Confronta
            </Button>
          </div>
        )}

        {/* ─── STEP 2: risultato + 2 CTA ─── */}
        {step === "result" && result && v && (() => {
          const Icon = v.icon;
          return (
            <div className="space-y-3" data-testid="confronta-step-result">
              <div className="rounded-xl border-2 p-4 space-y-3" style={{ background: v.bg, borderColor: v.border }} data-testid="confronta-result">
                <div className="flex items-center gap-2">
                  <Icon className="h-5 w-5" style={{ color: v.text }} />
                  <span className="font-bold text-sm" style={{ color: v.text }}>
                    {result.confronto.verdict === "conveniente" && "Conveniente! Risparmi"}
                    {result.confronto.verdict === "piu_caro" && "Più caro del prezzo attuale"}
                    {result.confronto.verdict === "stesso" && "Stesso prezzo del fornitore attuale"}
                    {result.confronto.verdict === "nessun_riferimento" && "Nessun prezzo attuale di confronto"}
                  </span>
                  {result.confronto.delta_percentuale != null && (
                    <span className="ml-auto text-lg font-black font-mono" style={{ color: v.text }} data-testid="confronta-delta-pct">
                      {result.confronto.delta_percentuale > 0 ? "+" : ""}{result.confronto.delta_percentuale.toFixed(1)}%
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="bg-white/60 rounded-lg p-2.5">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Nuova offerta</p>
                    <p className="font-mono font-bold text-base" data-testid="confronta-nuovo-prezzo">
                      <Euro className="h-3 w-3 inline" />{result.offerta.prezzo_per_unita_ivato.toFixed(4)}
                      <span className="text-xs text-slate-400 font-normal">/{result.ingrediente.unita_misura_riferimento}</span>
                    </p>
                    <p className="text-[10px] text-slate-400">ivato • netto: {result.offerta.prezzo_per_unita_netto.toFixed(4)}€</p>
                  </div>
                  <div className="bg-white/60 rounded-lg p-2.5">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Attuale</p>
                    {result.attuale.prezzo_per_unita_netto > 0 ? (
                      <>
                        <p className="font-mono font-bold text-base" data-testid="confronta-attuale-prezzo">
                          <Euro className="h-3 w-3 inline" />{result.attuale.prezzo_per_unita_netto.toFixed(4)}
                          <span className="text-xs text-slate-400 font-normal">/{result.ingrediente.unita_misura_riferimento}</span>
                        </p>
                        <p className="text-[10px] text-slate-400 truncate">{result.attuale.fornitore_nome || "—"}</p>
                      </>
                    ) : (
                      <p className="text-xs text-slate-400 italic">Nessun prezzo salvato</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  onClick={handleClose}
                  className="h-12 text-base font-bold"
                  data-testid="confronta-chiudi-btn"
                >
                  <X className="h-5 w-5 mr-2" /> Chiudi
                </Button>
                <Button
                  onClick={() => setStep("save")}
                  className="h-12 text-base font-bold"
                  data-testid="confronta-vai-salva-btn"
                >
                  <Save className="h-5 w-5 mr-2" /> Salva prezzo
                </Button>
              </div>
            </div>
          );
        })()}

        {/* ─── STEP 3: scelta fornitore + conferma ─── */}
        {step === "save" && result && (
          <div className="space-y-3" data-testid="confronta-step-save">
            <div className="rounded-xl bg-indigo-50/60 border border-indigo-200 p-3">
              <p className="text-xs text-indigo-700 font-semibold">
                Stai salvando <strong className="font-black">€{result.offerta.prezzo_per_unita_ivato.toFixed(4)}/{result.ingrediente.unita_misura_riferimento}</strong> ivato per <strong>{result.ingrediente.nome}</strong>
              </p>
            </div>

            <div>
              <label className="text-xs text-slate-600 mb-1 block font-medium">Seleziona fornitore esistente</label>
              <Select value={fornitoreId} onValueChange={(v) => { setFornitoreId(v); setFornitoreManuale(""); }}>
                <SelectTrigger className="h-12 text-base" data-testid="confronta-fornitore-select">
                  <SelectValue placeholder="Scegli dalla lista…" />
                </SelectTrigger>
                <SelectContent>
                  {fornitori.map(f => <SelectItem key={f.id} value={f.id} className="py-2.5">{f.ragione_sociale}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-[11px] text-slate-400 font-semibold uppercase">oppure</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>

            <div>
              <label className="text-xs text-slate-600 mb-1 block font-medium">Nuovo fornitore</label>
              <Input
                placeholder="Scrivi il nome…"
                value={fornitoreManuale}
                onChange={(e) => { setFornitoreManuale(e.target.value); setFornitoreId(""); }}
                className="h-12 text-base"
                data-testid="confronta-fornitore-manuale"
              />
            </div>

            <div className="grid grid-cols-2 gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setStep("result")}
                className="h-12 text-base font-bold"
                data-testid="confronta-back-btn"
              >
                Indietro
              </Button>
              <Button
                onClick={handleSalva}
                disabled={saving || (!fornitoreId && !fornitoreManuale.trim())}
                className="h-12 text-base font-bold"
                data-testid="confronta-salva-btn"
              >
                {saving ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Save className="h-5 w-5 mr-2" />}
                Conferma e salva
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ConfrontaPrezzoDialog;
